import { NOT_APPLICABLE } from 'constants/constants';
import { Column, ColumnBodyOptions, ColumnEditorOptions, ColumnProps } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Row } from 'primereact/row';
import React, { useState } from 'react';
import {
  useUpdateEpdSystemBoundaryModuleDeclared,
  useUpdateEpdSystemBoundaryModuleGeography,
  useUpdateSystemBoundaryMutation,
} from 'services/api/mutations';
import {
  useEPDDefinitionInformation,
  useEpdSystemBoundaryData,
  useEpdSystemBoundaryGrid,
  useGeographicalScopeLookup,
} from 'services/api/queries';
import styled from 'styled-components';
import { EPDProductType, LCAModules } from 'types/types';

import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import { validatedCellTemplate } from '../GridService';
import { ColumnFooterInputNumber, DataTableStyled } from '../StyledGrid';
import { SystemBoundaryGridHeader } from './SystemBoundaryGridHeader';

const SystemBoundaryGrid: React.FunctionComponent<{ epdId: string; epdVersionId: string; errors?: any }> = ({
  epdId,
  epdVersionId,
  errors,
}) => {
  const [isShareOfSpecificDataFocused, setIsShareOfSpecificDataFocused] = useState(false);
  const [isVariationProductsFocused, setIsVariationProductsFocused] = useState(false);
  const [isVariationSiteFocused, setIsVariationSiteFocused] = useState(false);
  const isReadOnly = useIsReadOnlyMode();

  const geographicalScopeLookup = useGeographicalScopeLookup().data;

  const data = useEpdSystemBoundaryGrid(epdVersionId).data;
  const boundaryData = useEpdSystemBoundaryData(epdVersionId).data;
  const epdDefinition = useEPDDefinitionInformation(epdVersionId).data;

  const rows = [
    { ord: 0, epdVersionId: data?.epdVersionId, module: 'Modules declared', ...data?.declaredModules },
    { ord: 1, epdVersionId: data?.epdVersionId, module: 'Geography', ...data?.declaredModulesGeography },
    {
      ...data?.declaredModulesGeography,
      isHidden: true,
    },
  ];

  const updateDeclaredModulesMutation = useUpdateEpdSystemBoundaryModuleDeclared(epdVersionId);
  const updateDeclaredModulesGeographyMutation = useUpdateEpdSystemBoundaryModuleGeography(epdVersionId);

  const updateSystemBoundaryMutation = useUpdateSystemBoundaryMutation(epdVersionId);
  const onChangeEpdSystemBoundaryData = async (propertyName: string, val: any) => {
    updateSystemBoundaryMutation.mutate({
      epdId: epdId,
      versionId: epdVersionId,
      propertyName: propertyName,
      newValue: val,
    });
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    if (options.rowIndex === 0) {
      const isRequiredStage = options.field === 'a1' || options.field === 'a2' || options.field === 'a3';
      const dropdownOptions = !isRequiredStage
        ? [
            { label: 'X', value: true },
            { label: 'ND', value: false },
          ]
        : [{ label: 'X', value: true }];
      return (
        <Dropdown
          value={options.rowData[options.field]}
          optionLabel="label"
          optionValue="value"
          options={dropdownOptions}
          onChange={(e: DropdownChangeEvent) => {
            updateDeclaredModulesMutation.mutate({
              epdVersionId: epdVersionId,
              fieldName: options.field,
              value: e.value,
            });
          }}
        />
      );
    }
    if (options.rowIndex === 1) {
      if (rows[0][options.field as keyof LCAModules<boolean | null>] === false) {
        return <span style={{ display: 'flex', justifyContent: 'center' }}>{NOT_APPLICABLE}</span>;
      }

      const geographyRow = document.querySelector('.p-datatable-tbody > tr:nth-child(2)');
      const height = (geographyRow?.clientHeight ?? 0) + 1;

      return (
        <Dropdown
          style={{ height }}
          filter
          value={options.rowData[options.value]}
          optionLabel="name"
          optionValue="id"
          options={geographicalScopeLookup}
          onChange={(e: DropdownChangeEvent) => {
            updateDeclaredModulesGeographyMutation.mutate({
              epdVersionId: epdVersionId,
              fieldName: options.field,
              value: e.value,
            });
          }}
          itemTemplate={(option) => {
            return <div style={{ maxWidth: '40rem', wordWrap: 'break-word', fontSize: '12px' }}>{option.name}</div>;
          }}
        />
      );
    }

    return options.rowData[options.field];
  };

  const modulesTemplate = (row: any, options: ColumnBodyOptions) => {
    if (options.rowIndex == 0) {
      if (row[options.field] == null) {
        return null;
      }
      return row[options.field] === true ? 'X' : 'ND';
    } else {
      if (rows[0][options.field as keyof LCAModules<boolean | null>] === false) {
        return NOT_APPLICABLE;
      }
      return geographicalScopeLookup?.find((x) => x.id === row[options.field])?.name;
    }
  };

  const cellTemplate = (row: any, options: ColumnBodyOptions) => {
    return validatedCellTemplate(modulesTemplate(row, options) as any);
  };

  const SystemBoundaryGridColumns: ColumnProps[] = [
    {
      field: 'module',
      header: 'Module',
      style: { minWidth: '10rem' },
    },
    {
      field: 'a1',
      header: 'A1',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'a2',
      header: 'A2',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'a3',
      header: 'A3',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'a4',
      header: 'A4',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'a5',
      header: 'A5',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b1',
      header: 'B1',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b2',
      header: 'B2',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b3',
      header: 'B3',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b4',
      header: 'B4',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b5',
      header: 'B5',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b6',
      header: 'B6',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'b7',
      header: 'B7',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'c1',
      header: 'C1',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'c2',
      header: 'C2',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'c3',
      header: 'C3',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'c4',
      header: 'C4',
      style: { minWidth: '3rem' },
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
    {
      field: 'd',
      header: 'D',
      editor: isReadOnly ? undefined : cellEditor,
      body: cellTemplate,
    },
  ];

  const spanColsCount = epdDefinition?.productType === EPDProductType.Goods ? 3 : 5;
  const disabledColsCount = SystemBoundaryGridColumns.length - 1 - spanColsCount;
  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="Share of specific data" />
        <Column
          colSpan={spanColsCount}
          style={{ padding: '2px' }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', marginRight: '10px', position: 'relative' }}>
              <ColumnFooterInputNumber
                mode="decimal"
                step={0.01}
                maxFractionDigits={2}
                min={0}
                max={100}
                suffix={isShareOfSpecificDataFocused ? undefined : '%'}
                onFocus={() => setIsShareOfSpecificDataFocused(true)}
                onBlur={() => setIsShareOfSpecificDataFocused(false)}
                value={boundaryData?.shareOfSpecificData ?? null}
                onValueChange={(e) => {
                  onChangeEpdSystemBoundaryData('shareOfSpecificData', e.value);
                }}
                disabled={isReadOnly}
              />
            </div>
          }
        />
        {Array.from({ length: disabledColsCount - 1 }, (_, index) => (
          <Column key={index} footer="-" style={{ backgroundColor: '#FCFCFC' }} />
        ))}
        <Column footer="-" style={{ borderBottom: 0 }} />
      </Row>
      <Row>
        <Column footer="Variation - products" />
        <Column
          colSpan={spanColsCount}
          style={{ padding: '2px' }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', marginRight: '10px', position: 'relative' }}>
              <ColumnFooterInputNumber
                mode="decimal"
                step={0.01}
                maxFractionDigits={2}
                min={0}
                max={100}
                suffix={isVariationProductsFocused ? undefined : '%'}
                onFocus={() => setIsVariationProductsFocused(true)}
                onBlur={() => setIsVariationProductsFocused(false)}
                value={boundaryData?.variationProducts ?? null}
                onValueChange={(e) => {
                  onChangeEpdSystemBoundaryData('variationProducts', e.value);
                }}
                disabled={isReadOnly}
              />
            </div>
          }
        />
        {Array.from({ length: disabledColsCount - 1 }, (_, index) => (
          <Column key={index} footer="-" style={{ backgroundColor: '#FCFCFC' }} />
        ))}
        <Column footer="-" style={{ borderBottom: 0 }} />
      </Row>
      <Row>
        <Column footer="Variation - sites" />
        <Column
          colSpan={spanColsCount}
          style={{ padding: '2px' }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', marginRight: '10px', position: 'relative' }}>
              <ColumnFooterInputNumber
                mode="decimal"
                step={0.01}
                maxFractionDigits={2}
                min={0}
                max={100}
                suffix={isVariationSiteFocused ? undefined : '%'}
                onFocus={() => setIsVariationSiteFocused(true)}
                onBlur={() => setIsVariationSiteFocused(false)}
                value={boundaryData?.variationSites ?? null}
                onValueChange={(e) => {
                  onChangeEpdSystemBoundaryData('variationSites', e.value);
                }}
                disabled={isReadOnly}
              />
            </div>
          }
        />
        {Array.from({ length: disabledColsCount - 1 }, (_, index) => (
          <Column key={index} footer="-" style={{ backgroundColor: '#FCFCFC' }} />
        ))}
        <Column footer="-" />
      </Row>
    </ColumnGroup>
  );

  return (
    <BoundaryDataTableStyled
      headerColumnGroup={SystemBoundaryGridHeader}
      footerColumnGroup={footerGroup}
      value={rows}
      dataKey="ord"
      tableStyle={{ minWidth: '49rem' }}
      showGridlines
      editMode="cell"
      rowClassName={(rowData) => (rowData.isHidden ? 'hidden-row' : '')}
    >
      {SystemBoundaryGridColumns?.map((x) => (
        <Column key={x.field} {...x} />
      ))}
    </BoundaryDataTableStyled>
  );
};

const BoundaryDataTableStyled = styled(DataTableStyled)`
  .p-datatable-thead > tr > th:last-child,
  .p-datatable-tbody > tr > td:last-child,
  .p-datatable-tfoot > tr > td:last-child {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
  }

  .p-datatable-thead > tr:first-child > th {
    ${(props) => props.theme.typography.contentTableTitleXs}
    background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
    padding: 0.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    ${(props) => props.theme.typography.contentTableBodyXs}
    background-color: ${(props) => props.theme.colors.primaryWhite};
    padding: 0.5rem 1rem;
  }

  .p-cell-editing .p-dropdown,
  .p-cell-editing .p-dropdown .p-inputtext {
    width: 100% !important;
    padding-left: 0;
    padding-right: 0;
  }

  .p-cell-editing .p-dropdown {
    position: relative;
  }

  .p-cell-editing .p-dropdown-trigger {
    width: 14px;
    height: 100%;
    position: absolute;
    right: 10px;
  }

  .p-cell-editing .p-dropdown .p-inputtext {
    padding: 0 0 0 10px;
  }

  .hidden-row {
    visibility: collapse;
  }
`;

export default SystemBoundaryGrid;
